/**
 * W4S Cart Share
 * ----------------------------------------------------------------------------
 * Packs the live cart into a link and rebuilds that cart when the link is opened,
 * on any browser or device. No app, no server: everything is in the link.
 *
 * Link:   {shop.url}/cart?w4s_cart=<payload>
 * Carries each line's variant, quantity, line-item properties (including hidden
 * "_" ones such as the Channel Kit Builder's _kit_builder) and selling plan, plus
 * the cart note. Cart attributes are only copied when listed in config.copyAttributes,
 * because apps (e.g. Intelligems) keep per-visitor data there.
 * Not carried: discount codes and prices. The rebuilt cart uses current prices.
 *
 * Payload: "1." + base64url(JSON), or "1z." + base64url(deflate-raw(JSON)) when the
 * plain version is long and the browser supports CompressionStream.
 * JSON: { i: [[variantId, quantity, properties|0, sellingPlanId|0], ...], n: "note", a: {attributes},
 *         q: "quote ref" }
 * q is optional: links made for a quote carry its number, and the rebuilt cart gets it as the
 * cart attribute config.quoteRefAttribute (default "quote_ref"), so it ends up on the order.
 *
 * Opening a link:
 * - Empty cart, or the same contents already: rebuilt (or left) without asking.
 * - Different cart: the shopper is asked before it's replaced.
 * - Lines that can't be added (sold out, deleted, unpublished) are skipped and listed.
 * - A loading view shows while the cart is rebuilt; it can't be dismissed until it's done.
 * Then the cart page reloads, or the cart drawer refreshes and opens.
 *
 * Public API (window.W4SCartShare):
 * - linkFor(cart, { quoteRef }) -> Promise<string|null>  link for a /cart.js object (null if empty);
 *   quoteRef (optional) is written to the rebuilt cart as the quote_ref attribute
 * - currentLink() -> Promise<string|null>  link for the live cart
 * Share field: <w4s-cart-share-field label="…"> shows the live cart's link in a read-only input
 * with a Copy button. It keeps the URL current as the cart changes, and on copy the URL fades to
 * "Link copied to your clipboard" and back. It has its own shadow root and styles, so it looks the
 * same on the cart page and inside the quote dialog.
 * Emits `w4s:cart-share:restored` on document with { detail: { added, skipped, replaced } }.
 */
(() => {
  if (window.W4SCartShare) return;

  const PARAM = 'w4s_cart';
  const VERSION = '1';
  const MAX_LINES = 250;
  const COMPRESS_OVER = 1500;

  const host = document.getElementById('w4s-cart-share');
  let root = host ? host.shadowRoot : null;
  if (host && !root) {
    const template = host.querySelector('template');
    if (template) {
      root = host.attachShadow({ mode: 'open' });
      root.appendChild(template.content.cloneNode(true));
      template.remove();
    }
  }

  let config = {};
  try {
    const configEl = root && root.querySelector('[data-config]');
    if (configEl) config = JSON.parse(configEl.textContent);
  } catch (e) { /* defaults below */ }

  const shopUrl = (config.shopUrl || window.location.origin).replace(/\/$/, '');
  const copyAttributes = Array.isArray(config.copyAttributes) ? config.copyAttributes : [];
  const attributionKey = config.attributionAttribute || '';
  const quoteRefKey = config.quoteRefAttribute === undefined ? 'quote_ref' : (config.quoteRefAttribute || '');
  const QUOTE_REF = /^[A-Za-z0-9][A-Za-z0-9_-]{0,39}$/;

  const cartRoot = () => {
    const cartRoute = (window.theme && window.theme.routes && window.theme.routes.cart) || '/cart';
    return cartRoute.replace(/\/$/, '');
  };

  const cartJson = async (path, body) => {
    const res = await fetch(`${cartRoot()}${path}`, {
      method: body ? 'POST' : 'GET',
      headers: body
        ? { 'Content-Type': 'application/json', Accept: 'application/json' }
        : { Accept: 'application/json' },
      body: body ? JSON.stringify(body) : undefined,
      credentials: 'same-origin'
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      const err = new Error(data.description || data.message || `Cart request failed (${res.status})`);
      err.status = res.status;
      throw err;
    }
    return data;
  };

  /* ------------------------------------------------------------- encoding */

  const toBase64Url = (bytes) => {
    let bin = '';
    for (let i = 0; i < bytes.length; i += 1) bin += String.fromCharCode(bytes[i]);
    return btoa(bin).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
  };

  const fromBase64Url = (text) => {
    const b64 = text.replace(/-/g, '+').replace(/_/g, '/');
    const bin = atob(b64 + '='.repeat((4 - (b64.length % 4)) % 4));
    const bytes = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i += 1) bytes[i] = bin.charCodeAt(i);
    return bytes;
  };

  const streamBytes = async (bytes, stream) => {
    const out = new Blob([bytes]).stream().pipeThrough(stream);
    return new Uint8Array(await new Response(out).arrayBuffer());
  };

  const canCompress = typeof CompressionStream === 'function';
  const canDecompress = typeof DecompressionStream === 'function';

  const cleanProperties = (properties) => {
    if (!properties || typeof properties !== 'object') return null;
    const out = {};
    Object.entries(properties).forEach(([key, value]) => {
      if (!key || value === null || value === undefined || value === '') return;
      out[key] = typeof value === 'string' ? value : String(value);
    });
    return Object.keys(out).length ? out : null;
  };

  // Compact, order-preserving description of a cart (what the link carries).
  const snapshot = (cart, quoteRef) => {
    const data = {
      i: (cart.items || []).slice(0, MAX_LINES).map((item) => [
        item.variant_id,
        item.quantity,
        cleanProperties(item.properties) || 0,
        (item.selling_plan_allocation && item.selling_plan_allocation.selling_plan.id) || 0
      ])
    };
    if (cart.note) data.n = cart.note;
    const attrs = {};
    copyAttributes.forEach((key) => {
      if (cart.attributes && cart.attributes[key]) attrs[key] = String(cart.attributes[key]);
    });
    if (Object.keys(attrs).length) data.a = attrs;
    if (quoteRef && QUOTE_REF.test(quoteRef)) data.q = quoteRef;
    return data;
  };

  const encode = async (data) => {
    const bytes = new TextEncoder().encode(JSON.stringify(data));
    const plain = `${VERSION}.${toBase64Url(bytes)}`;
    if (plain.length <= COMPRESS_OVER || !canCompress) return plain;
    try {
      const packed = await streamBytes(bytes, new CompressionStream('deflate-raw'));
      const zipped = `${VERSION}z.${toBase64Url(packed)}`;
      return zipped.length < plain.length ? zipped : plain;
    } catch (e) {
      return plain;
    }
  };

  const isPosInt = (n) => Number.isInteger(n) && n > 0;

  // Turns a payload back into validated lines. Throws on anything malformed.
  const decode = async (payload) => {
    const match = /^(\d+)(z?)\.([A-Za-z0-9_-]+)$/.exec(payload || '');
    if (!match || match[1] !== VERSION) throw new Error('Unrecognized cart link');
    let bytes = fromBase64Url(match[3]);
    if (match[2]) {
      if (!canDecompress) throw new Error('This browser can’t open compressed cart links');
      bytes = await streamBytes(bytes, new DecompressionStream('deflate-raw'));
    }
    const data = JSON.parse(new TextDecoder().decode(bytes));
    if (!data || !Array.isArray(data.i) || !data.i.length) throw new Error('Cart link is empty');

    const lines = data.i.slice(0, MAX_LINES).map((row) => {
      if (!Array.isArray(row)) throw new Error('Bad cart line');
      const [id, quantity, properties, sellingPlan] = row;
      if (!isPosInt(id) || !isPosInt(quantity) || quantity > 9999) throw new Error('Bad cart line');
      const line = { id, quantity };
      const props = properties && typeof properties === 'object' ? cleanProperties(properties) : null;
      if (props) line.properties = props;
      if (isPosInt(sellingPlan)) line.selling_plan = sellingPlan;
      return line;
    });

    const attributes = {};
    if (data.a && typeof data.a === 'object') {
      copyAttributes.forEach((key) => {
        if (typeof data.a[key] === 'string') attributes[key] = data.a[key];
      });
    }

    const quoteRef = typeof data.q === 'string' && QUOTE_REF.test(data.q) ? data.q : '';

    return {
      lines,
      note: typeof data.n === 'string' ? data.n.slice(0, 5000) : '',
      attributes,
      quoteRef
    };
  };

  const linkFor = async (cart, options = {}) => {
    if (!cart || !cart.items || !cart.items.length) return null;
    return `${shopUrl}${cartRoot()}?${PARAM}=${await encode(snapshot(cart, options.quoteRef))}`;
  };

  const currentLink = async () => linkFor(await cartJson('.js'));

  /* ------------------------------------------------------------- dialog UI */

  const dialog = root && root.getElementById('dialog');
  const views = root ? Array.from(root.querySelectorAll('[data-view]')) : [];
  let pendingConfirm = null;
  // While the cart is being rebuilt the dialog can't be dismissed (Esc, backdrop, close buttons).
  let locked = false;

  const showView = (name) => {
    views.forEach((v) => { v.hidden = v.dataset.view !== name; });
    if (dialog) dialog.setAttribute('aria-labelledby', `${name}-title`);
  };

  const openDialog = (name) => {
    if (!dialog) return;
    locked = name === 'loading';
    showView(name);
    if (typeof dialog.showModal === 'function') {
      if (!dialog.open) dialog.showModal();
    } else {
      dialog.setAttribute('open', '');
    }
    root.getElementById(`${name}-title`)?.focus();
  };

  const closeDialog = (force = false) => {
    if (locked && force !== true) return;
    locked = false;
    if (dialog && dialog.open) dialog.close();
  };

  const showLoading = () => openDialog('loading');

  if (dialog) {
    dialog.addEventListener('click', (evt) => { if (evt.target === dialog) closeDialog(); });
    dialog.addEventListener('cancel', (evt) => { if (locked) evt.preventDefault(); });
    dialog.addEventListener('close', () => {
      if (pendingConfirm) { pendingConfirm(false); pendingConfirm = null; }
    });
    root.querySelectorAll('[data-close]').forEach((btn) => btn.addEventListener('click', () => closeDialog()));
    // Confirming goes straight on to the loading view, so the dialog stays open.
    root.querySelector('[data-confirm]')?.addEventListener('click', () => {
      if (pendingConfirm) { pendingConfirm(true); pendingConfirm = null; }
    });
  }

  // Resolves true to replace the cart. Without the dialog, falls back to the browser's confirm.
  const askToReplace = (count) => {
    if (!dialog) return Promise.resolve(window.confirm('Replace the items in your cart with the shared cart?'));
    const text = root.querySelector('[data-confirm-text]');
    if (text) {
      const tpl = count === 1 ? text.dataset.templateOne : text.dataset.template;
      text.textContent = (tpl || text.dataset.template || '').replace('[count]', count);
    }
    return new Promise((resolve) => {
      pendingConfirm = resolve;
      openDialog('confirm');
    });
  };

  const showSkipped = (skipped, addedCount) => {
    if (!dialog) {
      window.alert(`Some items in the shared cart are no longer available:\n${skipped.map((s) => `- ${s.label}`).join('\n')}`);
      return Promise.resolve();
    }
    const list = root.querySelector('[data-skipped-list]');
    if (list) {
      list.textContent = '';
      skipped.forEach((s) => {
        const li = document.createElement('li');
        li.textContent = s.label;
        list.appendChild(li);
      });
    }
    const text = root.querySelector('[data-skipped-text]');
    if (text) {
      const tpl = addedCount ? text.dataset.template : text.dataset.templateNone;
      text.textContent = (tpl || '').replace('[count]', addedCount);
    }
    return new Promise((resolve) => {
      dialog.addEventListener('close', resolve, { once: true });
      openDialog('skipped');
    });
  };

  /* --------------------------------------------------------------- restore */

  const lineKey = (id, quantity, properties) => JSON.stringify([
    id,
    quantity,
    Object.entries(properties || {}).sort(([a], [b]) => (a < b ? -1 : 1))
  ]);

  const sameContents = (cart, lines) => {
    const have = (cart.items || []).map((i) => lineKey(i.variant_id, i.quantity, cleanProperties(i.properties))).sort();
    const want = lines.map((l) => lineKey(l.id, l.quantity, l.properties)).sort();
    return have.length === want.length && have.every((k, n) => k === want[n]);
  };

  const stripParam = () => {
    const url = new URL(window.location.href);
    url.searchParams.delete(PARAM);
    window.history.replaceState(window.history.state, '', url.pathname + url.search + url.hash);
    return url;
  };

  // Adds lines in one request; if that fails, one at a time so one bad line doesn't sink the rest.
  // /cart/add.js puts new lines at the top, so lines are sent in reverse to keep the sender's order.
  const addLines = async (lines) => {
    const ordered = lines.slice().reverse();
    try {
      await cartJson('/add.js', { items: ordered });
      return { added: lines.length, skipped: [] };
    } catch (e) { /* fall through to one by one */ }

    let added = 0;
    const skipped = [];
    for (const line of ordered) {
      try {
        await cartJson('/add.js', { items: [line] });
        added += 1;
      } catch (err) {
        skipped.unshift({ line, reason: err.message });
      }
    }
    return { added, skipped };
  };

  // Names for skipped lines, from the product JSON where the variant still exists.
  const labelSkipped = async (skipped) => Promise.all(skipped.map(async (s) => {
    const props = Object.entries(s.line.properties || {}).filter(([k]) => !k.startsWith('_'));
    const suffix = `${s.line.quantity > 1 ? ` × ${s.line.quantity}` : ''}${props.length ? ` (${props.map(([k, v]) => `${k}: ${v}`).join(', ')})` : ''}`;
    try {
      const res = await fetch(`/variants/${s.line.id}.js`, { headers: { Accept: 'application/json' } });
      if (res.ok) {
        const v = await res.json();
        const name = v.name || v.title;
        if (name) return { ...s, label: `${name}${suffix}` };
      }
    } catch (e) { /* fall back below */ }
    return { ...s, label: `An item that’s no longer listed${suffix}` };
  }));

  const showCart = () => {
    const onCartPage = window.location.pathname.replace(/\/$/, '') === cartRoot();
    if (onCartPage) {
      // The loading view stays up until the reloaded page replaces it.
      showLoading();
      window.location.reload();
      return;
    }
    if (document.querySelector('cart-drawer, #cart-drawer')) {
      closeDialog(true);
      document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh', { bubbles: true }));
      document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open'));
    } else {
      showLoading();
      window.location.href = cartRoot();
    }
  };

  const restore = async () => {
    const payload = new URLSearchParams(window.location.search).get(PARAM);
    if (!payload) return;
    stripParam();

    let shared;
    try {
      shared = await decode(payload);
    } catch (err) {
      console.warn('[W4S Cart Share] Could not read the cart link:', err);
      if (dialog) openDialog('invalid');
      return;
    }

    try {
      showLoading();
      const cart = await cartJson('.js');
      if (sameContents(cart, shared.lines)) {
        // Already the same cart: just record which quote it came from.
        if (quoteRefKey && shared.quoteRef) {
          await cartJson('/update.js', { attributes: { [quoteRefKey]: shared.quoteRef } }).catch(() => {});
        }
        closeDialog(true);
        return;
      }

      const replacing = cart.item_count > 0;
      if (replacing && !(await askToReplace(cart.item_count))) return;

      showLoading();
      document.documentElement.setAttribute('aria-busy', 'true');
      if (replacing) await cartJson('/clear.js', {});
      const { added, skipped } = await addLines(shared.lines);

      const update = {};
      if (shared.note) update.note = shared.note;
      const attributes = { ...shared.attributes };
      if (attributionKey) attributes[attributionKey] = 'yes';
      if (quoteRefKey && shared.quoteRef) attributes[quoteRefKey] = shared.quoteRef;
      if (Object.keys(attributes).length) update.attributes = attributes;
      if (Object.keys(update).length) await cartJson('/update.js', update).catch(() => {});

      document.dispatchEvent(new CustomEvent('w4s:cart-share:restored', {
        detail: { added, skipped: skipped.map((s) => s.line), replaced: replacing }
      }));

      if (skipped.length) {
        const labelled = await labelSkipped(skipped);
        locked = false;
        await showSkipped(labelled, added);
      }
      showCart();
    } catch (err) {
      console.error('[W4S Cart Share]', err);
      locked = false;
      if (dialog) openDialog('invalid');
    } finally {
      document.documentElement.removeAttribute('aria-busy');
    }
  };

  /* ------------------------------------------------------------- clipboard */

  // Copies the link once linkPromise resolves. Safari only allows clipboard writes during the
  // click, so where ClipboardItem exists the write starts now with the text still pending.
  const copyLink = async (linkPromise) => {
    if (navigator.clipboard && window.isSecureContext && typeof ClipboardItem === 'function' && navigator.clipboard.write) {
      try {
        const blob = linkPromise.then((link) => {
          if (!link) throw new Error('empty');
          return new Blob([link], { type: 'text/plain' });
        });
        await navigator.clipboard.write([new ClipboardItem({ 'text/plain': blob })]);
        return { link: await linkPromise, copied: true };
      } catch (err) {
        const link = await linkPromise;
        if (!link) return { link, copied: false };
        // Fall through to the older routes below.
        return { link, copied: await copyText(link).catch(() => false) };
      }
    }
    const link = await linkPromise;
    if (!link) return { link, copied: false };
    return { link, copied: await copyText(link).catch(() => false) };
  };

  const copyText = async (text) => {
    if (navigator.clipboard && window.isSecureContext) {
      try {
        await navigator.clipboard.writeText(text);
        return true;
      } catch (e) { /* try execCommand below */ }
    }
    const area = document.createElement('textarea');
    area.value = text;
    area.setAttribute('readonly', '');
    area.style.cssText = 'position:fixed;top:0;left:0;opacity:0;';
    document.body.appendChild(area);
    area.select();
    const ok = document.execCommand('copy');
    area.remove();
    return ok;
  };

  const FADE_MS = 200;
  const MESSAGE_MS = 2500;
  const reducedMotion = () => window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // Fades an input out, swaps its value, fades it back in.
  const swapValue = (input, value) => new Promise((resolve) => {
    if (reducedMotion()) {
      input.value = value;
      resolve();
      return;
    }
    input.style.opacity = '0';
    setTimeout(() => {
      input.value = value;
      input.style.opacity = '1';
      setTimeout(resolve, FADE_MS);
    }, FADE_MS);
  });

  /* ------------------------------------------------------------ share field */

  // Sizes follow the theme: 16px like its inputs (also stops iOS zooming on focus), 14px for the
  // label and button (the theme's 0.88em button size).
  // Styles live in the field's own shadow root and read the theme's CSS custom properties
  // (RGB triples, with fallbacks), so it matches the store on the cart page and in the quote
  // dialog without adding page-level CSS.
  const FIELD_CSS = `
    :host { display: block; }
    [hidden] { display: none !important; }
    .row { display: flex; align-items: stretch; width: 100%; }
    .label { display: block; margin: 0 0 6px; font-size: 14px; font-weight: 600; line-height: 1.4; text-align: left; }
    input {
      flex: 1 1 auto; min-width: 0; margin: 0;
      padding: var(--input-padding-y, 10px) 12px;
      border: var(--input-border-width, 1px) solid rgb(var(--input-text-color, var(--text-color, 17 17 17)) / 0.2);
      border-right: 0;
      border-radius: var(--input-border-radius, 0) 0 0 var(--input-border-radius, 0);
      background: rgb(var(--input-bg-color, var(--bg-color, 255 255 255)));
      color: rgb(var(--input-text-color, var(--text-color, 17 17 17)));
      font: inherit; font-size: 16px; line-height: 1.4;
      text-overflow: ellipsis;
      transition: opacity ${FADE_MS}ms ease;
      cursor: text;
    }
    input:focus { outline: none; border-color: rgb(var(--input-text-color, var(--text-color, 17 17 17)) / 0.6); }
    input.is-message { font-weight: 600; }
    button {
      flex: 0 0 auto; margin: 0; padding: 0 18px;
      border: var(--btn-border-width, 1px) solid rgb(var(--btn-bg-color, 17 17 17));
      border-radius: 0 var(--btn-border-radius, 0) var(--btn-border-radius, 0) 0;
      background: rgb(var(--btn-bg-color, 17 17 17));
      color: rgb(var(--btn-text-color, 255 255 255));
      font: inherit; font-size: 14px; font-weight: 700; line-height: 1.2;
      text-transform: var(--btn-text-transform, none);
      cursor: pointer;
      transition: opacity 0.3s ease;
    }
    button:focus-visible { outline: 2px solid rgb(var(--btn-bg-color, 17 17 17)); outline-offset: 2px; }
    @media (hover: hover) and (pointer: fine) { button:not(:disabled):hover { opacity: 0.85; } }
    button:disabled { opacity: 0.4; cursor: default; }
  `;

  const fields = new Set();
  let refreshTimer = null;

  // Recomputes the link in every field, debounced (cart events often come in bursts).
  const refreshFields = () => {
    clearTimeout(refreshTimer);
    refreshTimer = setTimeout(async () => {
      if (!fields.size) return;
      let link = null;
      try {
        link = await currentLink();
      } catch (err) {
        console.warn('[W4S Cart Share] Could not refresh the cart link', err);
        return;
      }
      fields.forEach((field) => field.setLink(link));
    }, 150);
  };

  class W4SCartShareField extends HTMLElement {
    connectedCallback() {
      if (!this.shadowRoot) {
        const root = this.attachShadow({ mode: 'open' });
        const labelText = this.getAttribute('label') || 'Share this cart';
        root.innerHTML = `
          <style>${FIELD_CSS}</style>
          <label class="label" for="link">${labelText.replace(/[<>&"]/g, '')}</label>
          <div class="row">
            <input id="link" type="text" readonly value="Creating link…" spellcheck="false" autocomplete="off">
            <button type="button" disabled>Copy</button>
          </div>
          <span class="status" role="status" style="position:absolute;width:1px;height:1px;overflow:hidden;clip:rect(0 0 0 0);white-space:nowrap;"></span>`;
        this.input = root.querySelector('input');
        this.button = root.querySelector('button');
        this.status = root.querySelector('.status');
        this.link = null;
        this.busy = false;
        // Click or focus selects the whole URL, for copying by hand.
        this.input.addEventListener('focus', () => this.input.select());
        this.input.addEventListener('click', () => this.input.select());
        this.button.addEventListener('click', () => this.copy());
      }
      fields.add(this);
      refreshFields();
    }

    disconnectedCallback() {
      fields.delete(this);
    }

    setLink(link) {
      this.link = link;
      if (this.busy) return;
      this.input.classList.remove('is-message');
      this.input.value = link || 'Your cart is empty';
      this.button.disabled = !link;
    }

    async flash(message) {
      this.busy = true;
      this.status.textContent = message;
      this.input.classList.add('is-message');
      await swapValue(this.input, message);
      clearTimeout(this.timer);
      this.timer = setTimeout(async () => {
        this.input.classList.remove('is-message');
        await swapValue(this.input, this.link || 'Your cart is empty');
        this.status.textContent = '';
        this.busy = false;
      }, MESSAGE_MS);
    }

    async copy() {
      if (this.busy || this.button.disabled) return;
      this.button.disabled = true;
      try {
        // Builds a fresh link from the live cart, so a copy is never stale.
        const { link, copied } = await copyLink(currentLink());
        this.link = link;
        if (!link) {
          this.setLink(null);
          return;
        }
        fields.forEach((field) => { if (field !== this) field.setLink(link); });
        if (copied) {
          await this.flash('Link copied to your clipboard');
        } else {
          this.input.value = link;
          this.input.focus();
          this.input.select();
          await this.flash('Press Ctrl+C or ⌘C to copy');
        }
      } catch (err) {
        console.error('[W4S Cart Share]', err);
        await this.flash('Couldn’t create the link. Please try again.');
      } finally {
        this.button.disabled = !this.link;
      }
    }
  }

  if (!customElements.get('w4s-cart-share-field')) {
    customElements.define('w4s-cart-share-field', W4SCartShareField);
  }

  // Keep the URL current as the cart changes.
  ['on:cart:add', 'on:line-item:change', 'on:cart-drawer:after-open', 'on:bfcache:load-restore',
    'w4s:channel-kit:added', 'w4s:cart-share:restored'].forEach((name) => {
    document.addEventListener(name, refreshFields);
  });
  // Opening the quote dialog (its field may have been built when the cart was different).
  document.addEventListener('click', (evt) => {
    if (evt.target instanceof Element && evt.target.closest('[data-w4s-quote-open]')) refreshFields();
  });

  window.W4SCartShare = { linkFor, currentLink, decode, PARAM };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', restore, { once: true });
  } else {
    restore();
  }
})();
